/**
 * The material engine behind a session's Prepare readings: the readings on
 * that session's plan, each saved in place and added as a draft.
 *
 * It reads the course's materials and their placements, joins them into one
 * item per listed material, and owns the writes that change the list (create,
 * remove). A row's own saves run through `useMaterialSave`; whatever a write
 * returns becomes the list's value at once, and both reads are then refreshed.
 */
import { useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useCourseMaterials } from "@/hooks/use-course-materials";
import { courseUnitsApi, type CourseUnit } from "@/lib/api/courseUnits";
import {
  courseMaterialsApi,
  type CreateMaterial,
  type Material,
  type MaterialCorrection,
  type ResourcePlacement,
} from "@/lib/api/courseMaterials";

import { classLabel, joinMaterials, type MaterialItem } from "./material-facts";

export const materialsKey = (courseId: string) => ["course-materials", courseId] as const;
export const placementsKey = (courseId: string) => ["course-resource-placements", courseId] as const;
export const historyKey = (courseId: string, placementId: string) =>
  ["course-resource-placement-history", courseId, placementId] as const;

export function useMaterialLibrary(courseId: string, {
  unitId,
  timezone = null,
}: {
  /** Only the materials on this class, for its Prepare readings. */
  unitId?: string;
  timezone?: string | null;
} = {}) {
  const queryClient = useQueryClient();
  const materials = useCourseMaterials(courseId, true);
  const placements = useQuery({
    queryKey: placementsKey(courseId),
    queryFn: () => courseMaterialsApi.getResourcePlacements(courseId),
  });
  const unitsQuery = useQuery({
    queryKey: ["course-units", courseId],
    queryFn: () => courseUnitsApi.list(courseId),
  });
  const units: CourseUnit[] = unitsQuery.data ?? [];
  // A row keeps its identity when a correction gives it a new material.
  const rowKeys = useRef(new Map<string, string>());

  const all = joinMaterials(materials.data ?? [], placements.data ?? []);
  const items = unitId === undefined ? all : all.filter((item) => item.material.unit_id === unitId);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: materialsKey(courseId) });
    void queryClient.invalidateQueries({ queryKey: placementsKey(courseId) });
  };

  const setMaterials = (change: (list: Material[]) => Material[]) => {
    queryClient.setQueryData<Material[]>(materialsKey(courseId), (list) => change(list ?? []));
  };

  const adoptPlacement = (placement: ResourcePlacement) => {
    queryClient.setQueryData<ResourcePlacement[]>(placementsKey(courseId), (list) => {
      const current = list ?? [];
      return current.some((item) => item.id === placement.id)
        ? current.map((item) => (item.id === placement.id ? { ...item, ...placement } : item))
        : [...current, placement];
    });
  };

  /** A material this view saved is the list's value at once. */
  const adoptMaterial = (saved: Material) => {
    setMaterials((list) => list.map((material) => (material.id === saved.id ? saved : material)));
    refresh();
  };

  /** A correction's new material takes the old one's row; the old one becomes history. */
  const adoptCorrection = (previousId: string, result: MaterialCorrection) => {
    rowKeys.current.set(result.material.id, rowKey(previousId));
    setMaterials((list) => list.map((material) => (material.id === previousId ? result.material : material)));
    adoptPlacement(result.placement);
    void queryClient.invalidateQueries({ queryKey: historyKey(courseId, result.placement.id) });
    refresh();
  };

  const rowKey = (materialId: string): string => rowKeys.current.get(materialId) ?? materialId;

  /** A new draft at the end of the course's order. */
  const create = async (body: Omit<CreateMaterial, "sort_order">): Promise<Material> => {
    const sortOrder = Math.max(-1, ...(materials.data ?? []).map((material) => material.sort_order)) + 1;
    const created = await courseMaterialsApi.createMaterial(courseId, { ...body, sort_order: sortOrder });
    // A syllabus is listed by its placement, so that is read before the
    // material joins the list; it never shows first as course material.
    if (body.placement_role === "syllabus") {
      await queryClient.invalidateQueries({ queryKey: placementsKey(courseId) });
    }
    setMaterials((list) => (list.some((material) => material.id === created.id) ? list : [...list, created]));
    refresh();
    return created;
  };

  const drop = (materialId: string) => {
    setMaterials((list) => list.filter((material) => material.id !== materialId));
    refresh();
  };

  return {
    courseId,
    items,
    isLoading: materials.isLoading || placements.isLoading,
    isError: materials.isError || placements.isError,
    retry: () => {
      void materials.refetch();
      void placements.refetch();
    },
    classLabel: (id: string) => classLabel(id, units, timezone),
    rowKey,
    adoptMaterial,
    adoptCorrection,
    refresh,
    create,
    drop,
  };
}

export type MaterialLibrary = ReturnType<typeof useMaterialLibrary>;
export type { MaterialItem };
